import React from "react";
import PropTypes from "prop-types";
import { Modal, Spin, message } from "antd";
import CompanyService from "services/company-service";
import { RCU_VERIFICATION_STATUS_TYPES } from "constants/enums";
import { Card, CardHead, Icon, ICONS, Pill, EmptyState } from "./ui";

/**
 * Applicant details verification (RCU).
 *
 * The same `identity-validation` flow the Case Details tab runs: phone and
 * email are verified with an OTP the applicant is sent, PAN and Aadhaar
 * against Signzy using the document already on file. The workspace showed the
 * applicants but not whether any of it had been verified, which is the one
 * thing an operator has to know before the kit goes out.
 *
 * Each identity keeps its own row so a failure is attributable: what was
 * checked, what came back, and the action that moves it on.
 */

const STATUS = RCU_VERIFICATION_STATUS_TYPES;

const toneFor = status => {
    if (status === STATUS.VERIFIED) return "ok";
    if (status === STATUS.FAILED) return "bad";
    if (status === STATUS.INITIATED) return "info";
    return "warn";
};

const labelFor = status => status || "Not started";

// The document masters each identity is verified against, as the Case Details
// tab picks them: PAN is document 7 or 12, Aadhaar 8 or 13.
const DOCUMENT_IDS = { pan: [7, 12], aadhar: [8, 13] };

const IDENTITIES = [
    { key: "phone", label: "Phone", valueKey: "phone_no", kind: "otp" },
    { key: "email", label: "Email", valueKey: "email_id", kind: "otp" },
    { key: "pan", label: "PAN", valueKey: "pan_no", kind: "signzy" },
    { key: "aadhar", label: "Aadhaar", valueKey: "aadhar_no", kind: "signzy" }
];

class ApplicantVerification extends React.Component {
    state = {
        // { row, identity, stage: "confirm" | "otp", otp, busy }
        modal: null,
        downloading: false,
        retrying: null
    };

    componentWillUnmount() {
        this.unmounted = true;
    }

    setSafeState(next) {
        if (!this.unmounted) this.setState(next);
    }

    documentFor(row, key) {
        const ids = DOCUMENT_IDS[key];
        if (!ids) return null;
        return (
            this.props.documents.find(
                doc =>
                    Number(doc.associateId) === Number(row.associate_id) &&
                    ids.includes(Number(doc.documentId))
            ) || null
        );
    }

    open = (row, identity) => {
        const detail = row[identity.key] || {};
        const stage =
            identity.kind === "otp" && detail.verification_status === STATUS.INITIATED
                ? "otp"
                : "confirm";
        this.setState({ modal: { row, identity, stage, otp: "", busy: false } });
    };

    close = () => this.setState({ modal: null });

    /** Uninitiated phone / email: ask the backend to send the OTP. */
    sendOtp = () => {
        const { row, identity } = this.state.modal;
        const requestId = (row[identity.key] || {}).request_id;
        this.setState(({ modal }) => ({ modal: { ...modal, busy: true } }));
        CompanyService.initiateValidation(requestId)
            .then(() => {
                message.success(`OTP sent to the applicant's ${identity.label.toLowerCase()}`);
                this.setSafeState(({ modal }) => ({
                    modal: { ...modal, stage: "otp", busy: false }
                }));
            })
            .catch(() => {
                message.error("Could not send the OTP");
                this.setSafeState(({ modal }) => ({ modal: { ...modal, busy: false } }));
            });
    };

    confirmOtp = () => {
        const { row, identity, otp } = this.state.modal;
        if (!otp) {
            message.error("Enter the OTP");
            return;
        }
        const requestId = (row[identity.key] || {}).request_id;
        this.setState(({ modal }) => ({ modal: { ...modal, busy: true } }));
        CompanyService.confirmPhoneEmailValidation(requestId, { verification_response: otp })
            .then(() => {
                message.success(`${identity.label} verified`);
                this.setSafeState({ modal: null });
                this.props.onRefresh();
            })
            .catch(() => {
                message.error("Could not confirm the OTP");
                this.setSafeState(({ modal }) => ({ modal: { ...modal, busy: false } }));
            });
    };

    /** PAN / Aadhaar against Signzy, using the document already on file. */
    verifyIdentity = () => {
        const { row, identity } = this.state.modal;
        const detail = row[identity.key] || {};
        const document = this.documentFor(row, identity.key);
        this.setState(({ modal }) => ({ modal: { ...modal, busy: true } }));
        CompanyService.verifyPanAadhaar({
            request_id: detail.request_id,
            name: row.associate,
            number: row[identity.valueKey],
            document_master_id: document && document.id
        })
            .then(() => {
                message.success(`${identity.label} sent for verification`);
                this.setSafeState({ modal: null });
                this.props.onRefresh();
            })
            .catch(() => {
                message.error(`Could not verify the ${identity.label}`);
                this.setSafeState(({ modal }) => ({ modal: { ...modal, busy: false } }));
            });
    };

    retry = (row, identity) => {
        const detail = row[identity.key] || {};
        this.setState({ retrying: `${row.associate_id}-${identity.key}` });
        CompanyService.retryVerification(detail.request_id)
            .then(() => {
                this.setSafeState({ retrying: null });
                this.props.onRefresh();
            })
            .catch(() => {
                message.error("Could not retry the verification");
                this.setSafeState({ retrying: null });
            });
    };

    download = () => {
        this.setState({ downloading: true });
        CompanyService.downloadRcuReport(this.props.caseId)
            .then(({ data }) => {
                const blob = new Blob([data], { type: "application/pdf" });
                const link = window.document.createElement("a");
                link.href = window.URL.createObjectURL(blob);
                link.download = `rcu-report-${this.props.caseId}.pdf`;
                window.document.body.appendChild(link);
                link.click();
                window.document.body.removeChild(link);
                this.setSafeState({ downloading: false });
            })
            .catch(() => {
                message.error("Failed to download the RCU report");
                this.setSafeState({ downloading: false });
            });
    };

    /** What an operator can do with this identity right now. */
    actionFor(row, identity) {
        const detail = row[identity.key];
        if (!detail) return null;
        const status = detail.verification_status;
        if (status === STATUS.VERIFIED) return null;
        if (identity.kind === "signzy") {
            if (!detail.document && !this.documentFor(row, identity.key)) {
                return { label: "Document not on file", disabled: true };
            }
            if (status === STATUS.FAILED) {
                return { label: "Retry", onClick: () => this.retry(row, identity) };
            }
            return { label: "Verify", onClick: () => this.open(row, identity) };
        }
        if (status === STATUS.INITIATED) {
            return { label: "Confirm OTP", onClick: () => this.open(row, identity) };
        }
        if (status === STATUS.FAILED) {
            return { label: "Retry", onClick: () => this.retry(row, identity) };
        }
        return { label: "Verify", onClick: () => this.open(row, identity) };
    }

    renderModal() {
        const modal = this.state.modal;
        if (!modal) return null;
        const { row, identity, stage } = modal;
        const otpStage = identity.kind === "otp" && stage === "otp";
        return (
            <Modal
                visible
                destroyOnClose
                className={"ops-ws-modal"}
                wrapClassName={"ops-ws-modal-wrap"}
                width={440}
                title={`Verify ${identity.label.toLowerCase()} — ${row.associate || "applicant"}`}
                okText={
                    identity.kind === "signzy" ? "Verify" : otpStage ? "Submit OTP" : "Send OTP"
                }
                confirmLoading={modal.busy}
                onOk={
                    identity.kind === "signzy"
                        ? this.verifyIdentity
                        : otpStage
                            ? this.confirmOtp
                            : this.sendOtp
                }
                onCancel={this.close}
            >
                <div className={"ops-ws__kv-list"}>
                    <div className={"ops-ws__param-row"}>
                        <span className={"ops-ws__param-label"}>{identity.label}</span>
                        <span className={"ops-ws__param-value"}>
                            {row[identity.valueKey] || "—"}
                        </span>
                    </div>
                    {identity.kind === "signzy" ? (
                        <div className={"ops-ws__hint"}>
                            {"Validated against Signzy using the document on file."}
                        </div>
                    ) : otpStage ? (
                        <div>
                            <div className={"ops-ws__field-label"}>{"OTP"}</div>
                            <input
                                className={"ops-ws__input"}
                                value={modal.otp}
                                placeholder={"1234"}
                                onChange={e =>
                                    this.setState({ modal: { ...modal, otp: e.target.value } })
                                }
                            />
                            <button
                                type={"button"}
                                className={"ops-ws__link"}
                                style={{ marginTop: 10 }}
                                onClick={this.sendOtp}
                            >
                                {"Resend OTP"}
                            </button>
                        </div>
                    ) : (
                        <div className={"ops-ws__hint"}>
                            {`An OTP will be sent to the applicant's ${identity.label.toLowerCase()}.`}
                        </div>
                    )}
                </div>
            </Modal>
        );
    }

    render() {
        const { rows, loading, canEdit } = this.props;
        return (
            <Card>
                <CardHead
                    title={"Applicant details verification"}
                    sub={"Phone and email by OTP, PAN and Aadhaar against Signzy."}
                    extra={
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <button
                                type={"button"}
                                className={"ops-ws__link"}
                                onClick={this.props.onRefresh}
                            >
                                <Icon d={ICONS.refresh} size={13} />
                                {"Refresh"}
                            </button>
                            <button
                                type={"button"}
                                className={"ops-ws__btn"}
                                disabled={this.state.downloading}
                                onClick={this.download}
                            >
                                <Icon d={ICONS.download} size={13} />
                                {this.state.downloading ? "Preparing…" : "RCU report"}
                            </button>
                        </div>
                    }
                />
                {loading && !rows.length ? (
                    <div className={"ops-ws__spin"}>
                        <Spin />
                    </div>
                ) : rows.length ? (
                    <div className={"ops-ws__table-scroll"}>
                        <table className={"ops-ws__table"}>
                            <thead>
                                <tr>
                                    <th>{"Applicant"}</th>
                                    <th>{"Identity"}</th>
                                    <th>{"Value"}</th>
                                    <th>{"Status"}</th>
                                    <th>{"Action"}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(row =>
                                    IDENTITIES.map((identity, index) => {
                                        const detail = row[identity.key];
                                        const action = this.actionFor(row, identity);
                                        const busy =
                                            this.state.retrying ===
                                            `${row.associate_id}-${identity.key}`;
                                        return (
                                            <tr key={`${row.associate_id}-${identity.key}`}>
                                                <td>{index === 0 ? row.associate || "—" : ""}</td>
                                                <td>{identity.label}</td>
                                                <td>{row[identity.valueKey] || "—"}</td>
                                                <td>
                                                    <Pill
                                                        tone={toneFor(
                                                            detail && detail.verification_status
                                                        )}
                                                    >
                                                        {labelFor(
                                                            detail && detail.verification_status
                                                        )}
                                                    </Pill>
                                                </td>
                                                <td>
                                                    {action ? (
                                                        <button
                                                            type={"button"}
                                                            className={"ops-ws__link"}
                                                            disabled={
                                                                action.disabled || !canEdit || busy
                                                            }
                                                            onClick={action.onClick}
                                                        >
                                                            {busy ? "Working…" : action.label}
                                                        </button>
                                                    ) : (
                                                        <span className={"ops-ws__hint"}>{"—"}</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className={"ops-ws__card-body"}>
                        <EmptyState>
                            {"No verification has been raised for these applicants yet."}
                        </EmptyState>
                    </div>
                )}
                {this.renderModal()}
            </Card>
        );
    }
}

ApplicantVerification.defaultProps = { rows: [], documents: [], onRefresh: () => {} };

ApplicantVerification.propTypes = {
    rows: PropTypes.array,
    documents: PropTypes.array,
    caseId: PropTypes.number,
    loading: PropTypes.bool,
    canEdit: PropTypes.bool,
    onRefresh: PropTypes.func
};

export default ApplicantVerification;
